import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Table from "../../components/ui/Table";
import Loader from "../../components/ui/Loader";
import EmptyState from "../../components/ui/EmptyState";
import { useApi } from "../../services/useApi";
import { useAuth } from "../auth/useAuth";
import { formatDateShort, formatDuration, formatTimeShort } from "../../utils/date";
import {
  getHiddenTeamSessions,
  getTeamSessions,
  setTeamSessionHiddenStatus,
  updateTeamSessionTitle,
} from "./teamService";
import { getSessionNotes } from "./sessionNotesService";
import { getRuntimeConfig } from "../../config/runtimeConfig";

const TeamSessionsPage = () => {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const api = useApi();
  const { token } = useAuth();
  const { openSessionInNewTab } = getRuntimeConfig();
  const [rows, setRows] = useState([]);
  const [hiddenRows, setHiddenRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notesBySession, setNotesBySession] = useState({});
  const [openMenuId, setOpenMenuId] = useState(null);
  const [editingRow, setEditingRow] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [savingTitle, setSavingTitle] = useState(false);
  const [titleError, setTitleError] = useState("");
  const [showRecycleBin, setShowRecycleBin] = useState(false);
  const [hiddenLoaded, setHiddenLoaded] = useState(false);
  const [loadingHidden, setLoadingHidden] = useState(false);
  const [savingHidden, setSavingHidden] = useState(false);
  const [openRecycleMenuId, setOpenRecycleMenuId] = useState(null);
  const [selectedHiddenIds, setSelectedHiddenIds] = useState([]);
  const [confirmRestore, setConfirmRestore] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const fetchNotesCounts = async (sessions) => {
    if (!sessions.length) return [];
    const entries = [];
    let index = 0;
    const limit = Math.min(6, sessions.length);
    const worker = async () => {
      while (index < sessions.length) {
        const current = sessions[index];
        index += 1;
        if (!current?.sessionId) {
          entries.push([current?.sessionId, 0]);
          continue;
        }
        try {
          const notesData = await getSessionNotes(
            api,
            current.sessionId,
            "TEAM",
          );
          entries.push([current.sessionId, (notesData?.notes || []).length]);
        } catch {
          entries.push([current.sessionId, 0]);
        }
      }
    };
    await Promise.all(Array.from({ length: limit }, () => worker()));
    return entries;
  };

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!token || !teamId) {
        setLoading(false);
        return;
      }
      try {
        const sessionsData = await getTeamSessions(api, teamId);
        if (!mounted) return;
        const sessions = sessionsData?.content || [];
        setRows(sessions.filter((s) => !s.isHidden));
        setHiddenRows([]);
        setHiddenLoaded(false);
        setLoading(false);

        const notesEntries = await fetchNotesCounts(sessions);
        if (!mounted) return;
        setNotesBySession(Object.fromEntries(notesEntries));
      } catch (err) {
        if (!mounted) return;
        setRows([]);
        setHiddenRows([]);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [api, token, teamId]);

  useEffect(() => {
    const onDocClick = () => setOpenMenuId(null);
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  const formatSessionTitle = (row) => {
    const title = (row.title || "").trim();
    if (title) return title;
    const date = row.createdAt ? new Date(row.createdAt) : null;
    if (!date) return row.place || "Session";
    const day = String(date.getDate()).padStart(2, "0");
    const month = date.toLocaleString("en-US", { month: "long" });
    const year = date.getFullYear();
    const place = row.place ? ` ${row.place}` : "";
    return `${day} ${month}, ${year}${place}`;
  };

  const handleMenuToggle = (e, id) => {
    e.stopPropagation();
    e.preventDefault();
    setOpenMenuId((current) => (current === id ? null : id));
  };

  const handleEditTitle = (e, row) => {
    e.stopPropagation();
    e.preventDefault();
    setOpenMenuId(null);
    setEditingRow(row);
    setEditTitle((row.title || "").trim());
    setTitleError("");
  };

  const handleRemoveHistory = (e, row) => {
    e.stopPropagation();
    e.preventDefault();
    if (!row.sessionId) return;
    setSavingHidden(true);
    setTeamSessionHiddenStatus(api, [row.sessionId], true)
      .then(() => {
        setRows((prev) => prev.filter((r) => r.sessionId !== row.sessionId));
        setHiddenRows((prev) => [row, ...prev]);
        setSelectedIds((prev) => prev.filter((id) => id !== row.sessionId));
        setOpenMenuId(null);
      })
      .finally(() => setSavingHidden(false));
  };

  const handleRestoreHidden = (row) => {
    if (!row?.sessionId) return;
    setConfirmRestore({ mode: "single", row });
  };

  const performRestoreSingle = async (row) => {
    setSavingHidden(true);
    try {
      await setTeamSessionHiddenStatus(api, [row.sessionId], false);
      setHiddenRows((prev) => prev.filter((r) => r.sessionId !== row.sessionId));
      setRows((prev) => [row, ...prev]);
      setSelectedHiddenIds((prev) => prev.filter((id) => id !== row.sessionId));
    } finally {
      setSavingHidden(false);
    }
  };

  const toggleHiddenSelection = (id) => {
    setSelectedHiddenIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const toggleAllHidden = () => {
    if (hiddenRows.length === 0) return;
    setSelectedHiddenIds((prev) =>
      prev.length === hiddenRows.length
        ? []
        : hiddenRows.map((r) => r.sessionId),
    );
  };

  const handleRestoreSelected = () => {
    if (selectedHiddenIds.length === 0) return;
    setConfirmRestore({ mode: "bulk" });
  };

  const performRestoreSelected = async () => {
    setSavingHidden(true);
    try {
      await setTeamSessionHiddenStatus(api, selectedHiddenIds, false);
      const restored = hiddenRows.filter((r) =>
        selectedHiddenIds.includes(r.sessionId),
      );
      setHiddenRows((prev) =>
        prev.filter((r) => !selectedHiddenIds.includes(r.sessionId)),
      );
      setRows((prev) => [...restored, ...prev]);
      setSelectedHiddenIds([]);
    } finally {
      setSavingHidden(false);
    }
  };

  const toggleSelection = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  useEffect(() => {
    let mounted = true;
    const loadHidden = async () => {
      if (!showRecycleBin || hiddenLoaded || !token || !teamId) return;
      setLoadingHidden(true);
      try {
        const hiddenData = await getHiddenTeamSessions(api, teamId);
        if (!mounted) return;
        const hiddenSessions = hiddenData?.content || [];
        setHiddenRows(hiddenSessions);
        setHiddenLoaded(true);
        const notesEntries = await fetchNotesCounts(hiddenSessions);
        if (!mounted) return;
        setNotesBySession((prev) => ({
          ...prev,
          ...Object.fromEntries(notesEntries),
        }));
      } finally {
        if (mounted) setLoadingHidden(false);
      }
    };
    loadHidden();
    return () => {
      mounted = false;
    };
  }, [api, hiddenLoaded, showRecycleBin, teamId, token]);

  const performDeleteSelected = async () => {
    if (selectedIds.length === 0) return;
    setSavingHidden(true);
    try {
      await setTeamSessionHiddenStatus(api, selectedIds, true);
      const removed = rows.filter((r) => selectedIds.includes(r.sessionId));
      setRows((prev) => prev.filter((r) => !selectedIds.includes(r.sessionId)));
      setHiddenRows((prev) => [...removed, ...prev]);
      setSelectedIds([]);
    } finally {
      setSavingHidden(false);
    }
  };

  const closeEditModal = () => {
    if (savingTitle) return;
    setEditingRow(null);
    setEditTitle("");
    setTitleError("");
  };

  const handleSaveTitle = async () => {
    if (!editingRow) return;
    const id = editingRow.sessionId;
    if (!id) return;
    setSavingTitle(true);
    setTitleError("");
    try {
      await updateTeamSessionTitle(api, id, editTitle.trim());
      setRows((prev) =>
        prev.map((row) =>
          row.sessionId === id ? { ...row, title: editTitle.trim() } : row,
        ),
      );
      closeEditModal();
    } catch (err) {
      setTitleError("Failed to update title. Please try again.");
    } finally {
      setSavingTitle(false);
    }
  };

  const NotesIcon = () => (
    <svg
      className="notes-icon"
      viewBox="0 0 24 24"
      width="16"
      height="16"
      aria-hidden="true"
    >
      <path
        d="M7 4h7l4 4v12a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z"
        fill="currentColor"
        opacity="0.2"
      />
      <path
        d="M14 4v4h4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path
        d="M8 12h8M8 16h8"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );

  const EditIcon = () => (
    <svg
      className="team-menu-icon"
      viewBox="0 0 24 24"
      width="16"
      height="16"
      aria-hidden="true"
    >
      <path
        d="M4 17.5V20h2.5l9-9-2.5-2.5-9 9z"
        fill="currentColor"
      />
      <path
        d="M14.5 6.5l2.5 2.5 1.5-1.5a1 1 0 0 0 0-1.4L16.4 4a1 1 0 0 0-1.4 0l-1.5 1.5z"
        fill="currentColor"
      />
    </svg>
  );

  const TrashIcon = () => (
    <svg
      className="team-menu-icon"
      viewBox="0 0 24 24"
      width="16"
      height="16"
      aria-hidden="true"
    >
      <path d="M6 7h12" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M9 7V5h6v2"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path
        d="M8 7l1 12h6l1-12"
        stroke="currentColor"
        strokeWidth="1.5"
      />
    </svg>
  );

  const BinIcon = () => (
    <svg
      className="team-menu-icon"
      viewBox="0 0 24 24"
      width="18"
      height="18"
      aria-hidden="true"
    >
      <path d="M6 7h12" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M9 7V5h6v2"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path
        d="M8 7l1 12h6l1-12"
        stroke="currentColor"
        strokeWidth="1.5"
      />
    </svg>
  );

  const RestoreIcon = () => (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
      <path
        d="M5 12a7 7 0 1 0 2-5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path
        d="M4 6v5h5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      />
    </svg>
  );

  const handleOpenSession = (row, event) => {
    if (!row?.sessionId || !teamId) return;
    const path = `/teams/${teamId}/sessions/${row.sessionId}`;
    if (event) {
      if (event.defaultPrevented) return;
      if (event.button !== undefined && event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
    }
    if (openSessionInNewTab) {
      window.open(
        `${window.location.origin}${path}`,
        "_blank",
        "noopener,noreferrer",
      );
      return;
    }
    navigate(path);
  };

  const columns = [
    {
      key: "sessionName",
      label: "Session",
      className: "cell-no-truncate",
      render: (row) => formatSessionTitle(row),
    },
    {
      key: "notes",
      label: "Notes",
      headerClassName: "cell-center",
      className: "cell-center cell-narrow",
      render: (row) => (
        <div
          className={`notes-count${
            (notesBySession[row.sessionId] ?? 0) === 0 ? " disabled" : ""
          }`}
        >
          <NotesIcon />
          <span>{notesBySession[row.sessionId] ?? 0}</span>
        </div>
      ),
    },
    {
      key: "createdAt",
      label: "Started",
      headerClassName: "cell-center",
      className: "cell-center",
      render: (row) =>
        `${formatDateShort(row.createdAt)} ${formatTimeShort(row.createdAt)}`,
    },
    {
      key: "endedAt",
      label: "Ended",
      headerClassName: "cell-center",
      className: "cell-center",
      render: (row) =>
        `${formatDateShort(row.endedAt)} ${formatTimeShort(row.endedAt)}`,
    },
    {
      key: "duration",
      label: "Duration",
      headerClassName: "cell-center",
      className: "cell-center cell-narrow",
      render: (row) => formatDuration(row.createdAt, row.endedAt),
    },
    {
      key: "place",
      label: "Place",
      headerClassName: "cell-center",
      className: "cell-truncate",
    },
    {
      key: "team",
      label: "Team",
      headerClassName: "cell-center",
      className: "cell-center",
      render: (row) => (
        <div className="cell-team">
          <img
            className="cell-team-photo"
            src={row.pictureUrl || "/icons/Logo_PNG.png"}
            alt={row.teamName || "Team"}
          />
          <div className="cell-team-meta">
            <div className="cell-team-name">{row.teamName || "Team"}</div>
          </div>
        </div>
      ),
    },
    {
      key: "menu",
      label: "",
      headerClassName: "cell-center",
      className: "cell-center",
      render: (row) => (
        <div className="team-menu" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            className="team-more"
            aria-label="Edit session"
            onClick={(e) => handleMenuToggle(e, row.sessionId)}
            onMouseDown={(e) => e.stopPropagation()}
          >
            ...
          </button>
          {openMenuId === row.sessionId && (
            <div className="team-menu-popover">
              <button type="button" onClick={(e) => handleEditTitle(e, row)}>
                <EditIcon />
                Edit Title
              </button>
              <button type="button" onClick={(e) => handleRemoveHistory(e, row)}>
                <TrashIcon />
                Remove History
              </button>
            </div>
          )}
        </div>
      ),
    },
    {
      key: "select",
      label: "",
      headerClassName: "cell-center",
      className: "cell-center",
      render: (row) => (
        <input
          type="checkbox"
          checked={selectedIds.includes(row.sessionId)}
          onClick={(e) => e.stopPropagation()}
          onChange={() => toggleSelection(row.sessionId)}
        />
      ),
      width: "36px",
    },
  ];


  return (
    <div>
      <div className="list-header">
        <h2>Team Sessions</h2>
        <button
          type="button"
          className="bin-button"
          onClick={() => setShowRecycleBin(true)}
          aria-label="Recycle bin"
        >
          <BinIcon />
        </button>
      </div>
      {selectedIds.length > 0 && (
        <div className="list-toolbar">
          <div className="list-selected">{selectedIds.length} selected</div>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => setConfirmDelete(true)}
          >
            Delete selected
          </button>
        </div>
      )}
      {loading && <Loader />}
      {!loading && rows.length === 0 && (
        <EmptyState title="No sessions" message="No team sessions found." />
      )}
      {!loading && rows.length > 0 && (
        <Table
          columns={columns}
          rows={rows}
          getRowHref={(row) =>
            row?.sessionId && teamId
              ? `/teams/${teamId}/sessions/${row.sessionId}`
              : undefined
          }
          onRowClick={handleOpenSession}
        />
      )}
      {showRecycleBin && (
        <div
          className="title-modal-backdrop"
        >
          <div
            className="recycle-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="title-modal-header">
              <div className="title-modal-title">Recycle Bin</div>
              <button
                type="button"
                className="title-modal-close"
                onClick={() => setShowRecycleBin(false)}
                disabled={savingHidden}
              >
                ×
              </button>
            </div>
            <div className="recycle-modal-body">
              <div className="recycle-toolbar">
                <label className="recycle-checkbox">
                  <input
                    type="checkbox"
                    checked={
                      hiddenRows.length > 0 &&
                      selectedHiddenIds.length === hiddenRows.length
                    }
                    onChange={toggleAllHidden}
                  />
                  <span>Select all</span>
                </label>
                {selectedHiddenIds.length > 0 && (
                  <div className="recycle-selected">
                    {selectedHiddenIds.length} selected
                  </div>
                )}
                <div className="recycle-bulk-actions">
                  <button
                    type="button"
                    className="icon-button"
                    onClick={handleRestoreSelected}
                    disabled={savingHidden || selectedHiddenIds.length === 0}
                    aria-label="Restore selected"
                  >
                    <RestoreIcon />
                  </button>
                  <button
                    type="button"
                    className="icon-button"
                    disabled
                    aria-label="Delete selected"
                  >
                    <TrashIcon />
                  </button>
                </div>
              </div>
              {loadingHidden && (
                <div className="recycle-empty">Loading hidden sessions...</div>
              )}
              {!loadingHidden && hiddenRows.length === 0 && (
                <div className="recycle-empty">No hidden sessions.</div>
              )}
              {hiddenRows.map((row) => (
                <div key={row.sessionId} className="recycle-row">
                  <label className="recycle-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedHiddenIds.includes(row.sessionId)}
                      onChange={() => toggleHiddenSelection(row.sessionId)}
                    />
                  </label>
                  <div className="recycle-title">{formatSessionTitle(row)}</div>
                  <div className="recycle-actions">
                    <button
                      type="button"
                      className="team-more"
                      aria-label="Edit recycle item"
                      onClick={() =>
                        setOpenRecycleMenuId((current) =>
                          current === row.sessionId ? null : row.sessionId,
                        )
                      }
                    >
                      ...
                    </button>
                    {openRecycleMenuId === row.sessionId && (
                      <div className="recycle-inline-menu">
                        <button
                          type="button"
                          onClick={() => handleRestoreHidden(row)}
                          disabled={savingHidden}
                        >
                          Restore
                        </button>
                        <button type="button" disabled>
                          Delete forever
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {confirmRestore && (
        <div
          className="title-modal-backdrop"
        >
          <div className="title-modal" onClick={(e) => e.stopPropagation()}>
            <div className="title-modal-header">
              <div className="title-modal-title">Confirm Restore</div>
              <button
                type="button"
                className="title-modal-close"
                onClick={() => setConfirmRestore(null)}
                disabled={savingHidden}
              >
                ×
              </button>
            </div>
            <div className="title-modal-body">
              {confirmRestore.mode === "single"
                ? "Restore this session?"
                : "Restore selected sessions?"}
            </div>
            <div className="title-modal-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setConfirmRestore(null)}
                disabled={savingHidden}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={async () => {
                  const current = confirmRestore;
                  setConfirmRestore(null);
                  if (current?.mode === "single" && current.row) {
                    await performRestoreSingle(current.row);
                  } else if (current?.mode === "bulk") {
                    await performRestoreSelected();
                  }
                }}
                disabled={savingHidden}
              >
                Restore
              </button>
            </div>
          </div>
        </div>
      )}
      {editingRow && (
        <div className="title-modal-backdrop">
          <div
            className="title-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="title-modal-header">
              <div className="title-modal-title">Edit Title</div>
              <button
                type="button"
                className="title-modal-close"
                onClick={closeEditModal}
                disabled={savingTitle}
              >
                ×
              </button>
            </div>
            <div className="title-modal-body">
              <label className="title-modal-label" htmlFor="team-title">
                Title
              </label>
              <input
                id="team-title"
                className="title-modal-input"
                value={editTitle}
                maxLength={50}
                onChange={(e) => setEditTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSaveTitle();
                }}
                placeholder="Enter session title"
              />
              {titleError && <div className="title-modal-error">{titleError}</div>}
            </div>
            <div className="title-modal-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={closeEditModal}
                disabled={savingTitle}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSaveTitle}
                disabled={savingTitle}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
      {confirmDelete && (
        <div
          className="title-modal-backdrop"
        >
          <div className="title-modal" onClick={(e) => e.stopPropagation()}>
            <div className="title-modal-header">
              <div className="title-modal-title">Confirm Delete</div>
              <button
                type="button"
                className="title-modal-close"
                onClick={() => setConfirmDelete(false)}
                disabled={savingHidden}
              >
                ×
              </button>
            </div>
            <div className="title-modal-body">
              Hide selected sessions?
            </div>
            <div className="title-modal-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setConfirmDelete(false)}
                disabled={savingHidden}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={async () => {
                  setConfirmDelete(false);
                  await performDeleteSelected();
                }}
                disabled={savingHidden}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamSessionsPage;

